import { Megaphone, MessageSquare, Smartphone, TriangleAlert } from 'lucide-react';
import {
  getApiError,
  usePublishNoticeMutation,
  useGetNoticeDeliveryQuery,
  useGetNoticePreviewQuery,
  type Notice,
  type NoticeDeliveryReport,
  type NoticePreview,
} from '@/store/api';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const stampFmt = new Intl.DateTimeFormat('en-GH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/*
  One dialog, two jobs, because they are the same question at two points in time.

  BEFORE publishing it answers "who would this reach, and what will it cost" —
  and it is the thing you are meant to look at before pressing the button, so
  the button lives here rather than on the list where it could be pressed
  without reading anything.

  AFTER publishing it answers "where did it actually go" from the delivery
  ledger. Same screen, so nobody has to learn two.
*/
export function NoticeReachDialog({
  notice,
  institutionId,
  onClose,
  onPublished,
}: {
  notice: Notice | null;
  institutionId: string;
  onClose: () => void;
  onPublished: (message: string) => void;
}) {
  const published = notice?.status === 'published' || notice?.status === 'cancelled';

  const { data: preview, isLoading: previewLoading } = useGetNoticePreviewQuery(
    { institutionId, noticeId: notice?._id ?? '' },
    { skip: !notice || published },
  );
  const { data: report, isLoading: reportLoading } = useGetNoticeDeliveryQuery(
    { institutionId, noticeId: notice?._id ?? '' },
    { skip: !notice || !published },
  );
  const [publishNotice, { isLoading: publishing }] = usePublishNoticeMutation();

  const handlePublish = async () => {
    if (!notice) return;
    try {
      const res = await publishNotice({ institutionId, noticeId: notice._id }).unwrap();
      onPublished(
        res.result.smsSuppressed
          ? `"${notice.title}" is live in the app. SMS was requested but is switched off on this server.`
          : `"${notice.title}" is live — ${res.result.pushSent} push, ${res.result.smsSent} SMS.`,
      );
      onClose();
    } catch (err) {
      onPublished(getApiError(err));
      onClose();
    }
  };

  return (
    <Modal
      open={notice !== null}
      onClose={onClose}
      size="lg"
      title={published ? 'Where it went' : 'Who this reaches'}
      description={notice?.title}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {!published && (
            <Button icon={<Megaphone size={13} />} loading={publishing} onClick={() => void handlePublish()}>
              Publish now
            </Button>
          )}
        </div>
      }
    >
      {(published ? reportLoading : previewLoading) ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : published && report ? (
        <DeliveryReport report={report} />
      ) : preview ? (
        <Preview preview={preview} />
      ) : null}
    </Modal>
  );
}

function Preview({ preview }: { preview: NoticePreview }) {
  const smsWillSend = preview.sms.enabled && preview.sms.serverEnabled;

  return (
    <div className="flex flex-col gap-3">
      <div className={cn(styles.panel, 'px-3 py-2.5')}>
        <p className={styles.label}>Reaches</p>
        <p className={cn(styles.figure, 'mt-0.5 text-[20px]')}>{preview.recipientCount} parents</p>
        <p className={styles.meta}>
          covering {preview.childCount} child{preview.childCount === 1 ? '' : 'ren'} — one message each,
          however many children they have
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {/* In-app is the notice itself, so it always reaches everyone. */}
        <Channel
          icon={<Megaphone size={13} />}
          name="In the parent app"
          tone="success"
          status={`All ${preview.inApp.reach}`}
          note="Always on — the notice IS the entry in their app."
        />

        <Channel
          icon={<Smartphone size={13} />}
          name="Push notification"
          tone={!preview.push.enabled ? 'neutral' : !preview.push.configured ? 'warning' : 'success'}
          status={
            !preview.push.enabled ? 'Off' : !preview.push.configured ? 'Not configured' : `${preview.push.reachable}`
          }
          note={
            !preview.push.enabled
              ? 'Not selected for this notice.'
              : !preview.push.configured
                ? 'Firebase is not set up on this server, so no push will be delivered. The notice still appears in the app.'
                : `${preview.push.unreachable} parent(s) have no app installed yet.`
          }
        />

        <Channel
          icon={<MessageSquare size={13} />}
          name="SMS"
          tone={!preview.sms.enabled ? 'neutral' : !preview.sms.serverEnabled ? 'warning' : 'danger'}
          status={!preview.sms.enabled ? 'Off' : !preview.sms.serverEnabled ? 'Blocked' : `${preview.sms.reachable}`}
          note={
            !preview.sms.enabled
              ? 'Not selected for this notice.'
              : !preview.sms.serverEnabled
                ? 'NOTICE_SMS_ENABLED is off on this server, so no SMS will be sent. The notice still publishes in the app.'
                : `${preview.sms.reachable} messages will be sent and billed. Cap is ${preview.sms.cap} per notice.`
          }
        />
      </div>

      {smsWillSend && (
        <div>
          <p className={cn(styles.label, 'mb-1.5')}>The SMS parents receive</p>
          <p className="rounded-md border border-border bg-muted px-2.5 py-2 text-[11px] leading-relaxed text-foreground">
            {preview.sms.sampleMessage}
          </p>
        </div>
      )}

      {smsWillSend && (
        <p className={cn(styles.markWarning, 'w-full justify-start normal-case tracking-normal text-[11px]')}>
          <TriangleAlert size={12} className="flex-shrink-0" />
          Publishing sends {preview.sms.reachable} SMS. This cannot be undone.
        </p>
      )}
    </div>
  );
}

function Channel({
  icon,
  name,
  status,
  note,
  tone,
}: {
  icon: React.ReactNode;
  name: string;
  status: string;
  note: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
}) {
  return (
    <div className={cn(styles.panel, 'flex items-start justify-between gap-3 px-3 py-2')}>
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-[11.5px] font-semibold text-foreground">
          {icon}
          {name}
        </p>
        <p className={cn(styles.meta, 'mt-0.5')}>{note}</p>
      </div>
      <Mark tone={tone}>{status}</Mark>
    </div>
  );
}

function DeliveryReport({ report }: { report: NoticeDeliveryReport }) {
  const count = (channel: string, status: string) =>
    report.deliveries.filter((d) => d.channel === channel && d.status === status).length;

  return (
    <div className="flex flex-col gap-3">
      <p className={styles.meta}>
        Published {report.publishedAt ? stampFmt.format(new Date(report.publishedAt)) : '—'} ·{' '}
        {report.deliveries.length} delivery attempt{report.deliveries.length === 1 ? '' : 's'} recorded
      </p>

      <div className="grid grid-cols-4 gap-2">
        {[
          { label: 'SMS sent', value: count('sms', 'sent') },
          { label: 'SMS failed', value: count('sms', 'failed') },
          { label: 'Push sent', value: count('push', 'sent') },
          { label: 'Skipped', value: report.deliveries.filter((d) => d.status === 'skipped').length },
        ].map((f) => (
          <div key={f.label} className={cn(styles.panel, 'px-3 py-2')}>
            <p className={styles.label}>{f.label}</p>
            <p className={cn(styles.figure, 'mt-0.5 text-[16px]')}>{f.value}</p>
          </div>
        ))}
      </div>

      {report.deliveries.length === 0 ? (
        <p className={styles.meta}>
          No push or SMS was attempted — this notice went to the app only.
        </p>
      ) : (
        <div className={cn(styles.panel, 'divide-y divide-border')}>
          {report.deliveries.map((d) => (
            <div key={d._id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <p className="text-[11.5px] font-semibold text-foreground">
                  {d.recipientKey}
                  <span className="ml-1.5 font-normal text-muted-foreground">
                    {d.channel} · {d.childCount} child{d.childCount === 1 ? '' : 'ren'}
                  </span>
                </p>
                {(d.error || d.sentAt) && (
                  <p className={styles.meta}>{d.error || (d.sentAt ? stampFmt.format(new Date(d.sentAt)) : '')}</p>
                )}
              </div>
              <Mark
                tone={d.status === 'sent' ? 'success' : d.status === 'failed' ? 'danger' : 'neutral'}
              >
                {d.status}
              </Mark>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
